import { userSettingsActions } from '../stores/user-settings';
import type { UserSettings } from '../types/user-settings';

// Type declaration for the legacy TL global (web/TL/main.js)
declare global {
  interface Window {
    TL?: {
      REST_URL: string;
      buildAuthHeaders: () => Record<string, string>;
    };
  }
}

let initialized = false;

/**
 * Initialize the bridge between legacy Box.Application and modern stores
 * Should be called after 'auth-success' event
 */
export function initBoxBridge(): void {
  if (initialized) {
    console.warn('[TLShared] Box bridge already initialized');
    return;
  }

  if (!window.Box?.Application) {
    console.error('[TLShared] Box.Application not available');
    return;
  }

  try {
    const dataManager = window.Box.Application.getService('data-manager');
    const backboneSettings = dataManager.getUserSettings();

    // Initial sync: Legacy -> Modern
    const settings = backboneSettings.toJSON() as UserSettings;
    userSettingsActions._setFromLegacy(settings);
    console.log('[TLShared] UserSettings initialized from legacy');

    // Listen for legacy changes
    backboneSettings.on('change', () => {
      const updated = backboneSettings.toJSON() as UserSettings;
      userSettingsActions._setFromLegacy(updated);
      console.log('[TLShared] UserSettings updated from legacy change');
    });

    initialized = true;
    console.log('[TLShared] Box bridge initialized');
  } catch (e) {
    console.error('[TLShared] Failed to initialize box bridge:', e);
  }
}

/**
 * Get a service from Box.Application
 */
export function getService<T = any>(serviceName: string): T {
  if (!window.Box?.Application) {
    throw new Error('[TLShared] Box.Application not available');
  }
  return window.Box.Application.getService(serviceName);
}

/**
 * Broadcast a message to legacy modules
 */
export function broadcast(messageName: string, data?: any): void {
  if (!window.Box?.Application) {
    console.warn('[TLShared] Box.Application not available for broadcast');
    return;
  }
  window.Box.Application.broadcast(messageName, data);
}

/**
 * Check if bridge is initialized
 */
export function isInitialized(): boolean {
  return initialized;
}

/**
 * Get the legacy REST API base URL (TL.REST_URL, e.g. ".../tracking_live_exporter/rest/v3/")
 */
export function getRestBaseUrl(): string {
  if (!window.TL?.REST_URL) {
    throw new Error('[TLShared] TL.REST_URL not available');
  }
  return window.TL.REST_URL;
}

/**
 * Build the auth headers for a REST call, via the legacy TL.buildAuthHeaders()
 * (Authorization: Bearer <jwt>, plus Impersonate when set) - reused rather than
 * re-derived, so behavior (including impersonation) stays identical to the legacy path.
 */
export function buildAuthHeaders(): Record<string, string> {
  if (!window.TL?.buildAuthHeaders) {
    throw new Error('[TLShared] TL.buildAuthHeaders not available');
  }
  return window.TL.buildAuthHeaders();
}

/**
 * Reset bridge state (for testing)
 */
export function _resetBridge(): void {
  initialized = false;
  userSettingsActions.clear();
}
